import React, {useEffect, useRef} from "react";
import {Box, Chip, CircularProgress, IconButton, TextField} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import ListItemReversedTextView from "../../../../../common/components/ListItemReversedTextView";
import {useUserTranslationsView} from "../hooks/useUserTranslationsView";

export interface TranslationsViewProps {
    wordId: string;
    translations: string[];
    userTranslations?: string[];
}

export default function TranslationsView({wordId, translations, userTranslations = []}: TranslationsViewProps) {

    const translationInputRef = useRef<HTMLInputElement | null>(null);

    const {
        isEditingUserTranslations,
        currentUserTranslations,
        editedUserTranslations,
        currentlyEditedUserTranslation,
        isSavingUserTranslations,
        startEditing,
        cancelEditing,
        saveEditing,
        addEditedTranslation,
        removeEditedTranslation,
        setCurrentlyEditedUserTranslation,
        handleInputKeyDown,
    } = useUserTranslationsView({
        wordId,
        translations,
        userTranslations,
    });

    useEffect(() => {
        if (isEditingUserTranslations) {
            translationInputRef.current?.focus();
        }
    }, [isEditingUserTranslations]);

    const titleText = `Translation${translations.length > 1 ? "s" : ""}`;

    const buttonSx = {
        width: 26,
        height: 26,
        ml: 0.5,
        // border: "1px solid rgba(0, 0, 0, 0.12)",
        // backgroundColor: (theme: Theme) => theme.palette.secondary.main,
        '&:hover': {
            backgroundColor: "rgba(0, 0, 0, 0.06)",
            borderColor: "rgba(0, 0, 0, 0.2)",
        },
        '&.Mui-disabled': {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
            borderColor: "rgba(0, 0, 0, 0.08)",
        },
    } as const;

    const titleButtonsForNotEditingUserTranslations = (
        <IconButton size={"small"} sx={buttonSx} onClick={startEditing}>
            {((currentUserTranslations.length) > 0) ? (
                <EditIcon fontSize={"small"} />
            ) : (
                <AddIcon fontSize={"small"}/>
            )}
        </IconButton>
    );

    const titleButtonsForEditingUserTranslations = isSavingUserTranslations ? (
        <CircularProgress size={18} sx={{color: "secondary.main"}} />
    ) : (
        <>
            <IconButton size={"small"} sx={buttonSx} onClick={() => {
                void saveEditing();
            }} disabled={isSavingUserTranslations}>
                <CheckIcon fontSize={"small"} sx={{color: "black"}}/>
            </IconButton>
            <IconButton size={"small"} sx={buttonSx} onClick={cancelEditing}>
                <CloseIcon fontSize={"small"} sx={{color: "black"}}/>
            </IconButton>
        </>
    );

    const title = (
        <Box component="span" sx={{display: "inline-flex", alignItems: "center", gap: 0.5, lineHeight: 1.2, verticalAlign: "middle"}}>
            <Box component="span" sx={{display: "inline-flex", alignItems: "center"}}>{titleText}</Box>
            {isEditingUserTranslations ? titleButtonsForEditingUserTranslations : titleButtonsForNotEditingUserTranslations}
        </Box>
    );

    const contentForNotEditingUserTranslations =
        translations.concat(currentUserTranslations).join(", ");

    const chipSx = {
        ml: 0.5,
        mt: 0,
        borderColor: 'secondary.main',
        color: 'black',
        backgroundColor: 'primary.main',
        '& .MuiChip-deleteIcon': {
            color: 'black',
        },
    } as const;

    const contentForEditingUserTranslations = (
        <Box>
            <Box sx={{display: 'block', mb: editedUserTranslations.length > 0 ? 1 : 0}}>{translations.join(", ")}</Box>
            {editedUserTranslations.length > 0 && (
                <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', rowGap: 0.5, columnGap: 0.5, mb: 1}}>
                    {editedUserTranslations.map((translation, index) => (
                        <Chip key={index}
                              label={translation}
                              size="small"
                              sx={chipSx}
                              onDelete={() => removeEditedTranslation(index)}
                        />
                    ))}
                </Box>
            )}
            <TextField
                inputRef={translationInputRef}
                variant="outlined"
                spellCheck={true}
                autoCapitalize="off"
                fullWidth={true}
                value={currentlyEditedUserTranslation}
                size="small"
                onChange={(e) => setCurrentlyEditedUserTranslation(e.target.value)}
                onKeyDown={(e) => void handleInputKeyDown(e)}
                InputProps={{
                    style: {fontSize: 16},
                    endAdornment: (
                        <IconButton
                            title={"Add translation"}
                            color="secondary"
                            sx={buttonSx}
                            onClick={() => {
                                addEditedTranslation();
                                requestAnimationFrame(() => translationInputRef.current?.focus());
                            }}
                        >
                            <AddIcon fontSize={"small"} sx={{color: 'black'}}/>
                        </IconButton>
                    ),
                }}
                sx={{
                    flexGrow: 1,
                    mt: 1,
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                            borderColor: 'secondary.main',
                        },
                        '&:hover fieldset': {
                            borderColor: 'secondary.main',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: 'secondary.light',
                        },
                    },
                }}
            />
        </Box>
    );

    const content = isEditingUserTranslations ? contentForEditingUserTranslations : contentForNotEditingUserTranslations;

    return (
        <ListItemReversedTextView title={title} content={content}/>
    );
}